/**
 * ParticipantsModal Component
 * Shows a modal with all ritual members (joined) and saved users in a grid card layout
 */

import { MoreVertical, Search } from "lucide-react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { ChatBubbleIcon, HeartFilledIcon, HeartOutlineIcon, WarningIcon } from "@/components/icons"
import { ReportModal } from "@/components/shared/ReportModal"
import { Avatar } from "@/components/ui/avatar"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useCreateDmConversation } from "@/features/chats/hooks/useDmChat"
import {
  useFollowUser,
  useRitualMembers,
  useRitualSavedUsers,
  useUnfollowUser,
} from "../../hooks/useRitualMembers"
import type { RitualMember, RitualSavedUser } from "../../types/detail.types"

interface ParticipantsModalProps {
  ritualId: string
  currentUserId?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type Tab = "joined" | "saved"

export function ParticipantsModal({
  ritualId,
  currentUserId,
  open,
  onOpenChange,
}: ParticipantsModalProps) {
  const [page, setPage] = useState(1)
  const [savedPage, setSavedPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState("")
  const [activeTab, setActiveTab] = useState<Tab>("joined")
  const navigate = useNavigate()
  const { mutate: createConversation, isPending: isCreatingConversation } =
    useCreateDmConversation()
  const { data, isLoading } = useRitualMembers(ritualId, page)
  const { data: savedData, isLoading: isSavedLoading } = useRitualSavedUsers(ritualId, savedPage)
  const { followUser, isFollowing } = useFollowUser()
  const { unfollowUser, isUnfollowing } = useUnfollowUser()
  const [reportUser, setReportUser] = useState<RitualMember | RitualSavedUser | null>(null)

  const filteredMembers = data?.members.filter(member => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return member.name?.toLowerCase().includes(q) || member.username?.toLowerCase().includes(q)
  })

  const filteredSavedUsers = savedData?.users.filter(user => {
    if (!searchQuery.trim()) return true
    const q = searchQuery.toLowerCase()
    return user.name?.toLowerCase().includes(q) || user.username?.toLowerCase().includes(q)
  })

  const handleFollowToggle = (e: React.MouseEvent, user: RitualMember | RitualSavedUser) => {
    e.stopPropagation()
    if (user.isFollowing) {
      unfollowUser(user.id)
    } else {
      followUser(user.id)
    }
  }

  const handleMemberClick = (user: RitualMember | RitualSavedUser) => {
    navigate(`/profile/${user.id}`)
  }

  const handleLoadMore = () => {
    if (activeTab === "joined" && data && page < data.totalPages) {
      setPage(p => p + 1)
    } else if (activeTab === "saved" && savedData && savedPage < savedData.totalPages) {
      setSavedPage(p => p + 1)
    }
  }

  const currentData = activeTab === "joined" ? data : savedData
  const isCurrentLoading = activeTab === "joined" ? isLoading : isSavedLoading
  const filteredUsers = activeTab === "joined" ? filteredMembers : filteredSavedUsers

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lg:max-w-[1300px] lg:max-h-[970px] h-full rounded-2xl p-0 overflow-hidden bg-[#0f0f0f] border-[#F2F0EB4D] flex flex-col gap-5">
        <div className="px-7.5 pt-8.5">
          <div className="flex items-center gap-3.75 mb-6.25">
            <DialogTitle className="text-[18px] leading-7.5 font-semibold text-primary">
              Participants
            </DialogTitle>
            <div className="w-px h-[30px] bg-[#F2F0EB1A]" />
            <div className="flex items-center gap-5">
              <button
                className={`text-sm cursor-pointer transition-colors ${
                  activeTab === "joined"
                    ? "text-white font-semibold"
                    : "text-[#FFFFFFB3] font-normal"
                }`}
                onClick={() => {
                  setActiveTab("joined")
                  setPage(1)
                }}
              >
                Joined ({data?.total ?? 0})
              </button>
              <button
                className={`text-sm cursor-pointer transition-colors ${
                  activeTab === "saved"
                    ? "text-white font-semibold"
                    : "text-[#FFFFFFB3] font-normal"
                }`}
                onClick={() => {
                  setActiveTab("saved")
                  setSavedPage(1)
                }}
              >
                Saved ({savedData?.total ?? 0})
              </button>
            </div>
          </div>

          <div className="flex items-center h-[65px] px-[14px] rounded-[15px] border border-[#F2F0EB26] bg-[#F2F0EB0D]">
            <Search className="w-5 h-5 text-white/90 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-[14px] font-normal text-white/90 placeholder:text-white/90 placeholder:font-normal px-3"
            />
          </div>
        </div>

        <div className="overflow-y-auto px-6 pb-6">
          {isCurrentLoading && (activeTab === "joined" ? page === 1 : savedPage === 1) ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-olive" />
            </div>
          ) : filteredUsers && filteredUsers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between gap-3 p-5 rounded-[20px] cursor-pointer bg-[#FFFFFF0D] border border-[#FFFFFF1A] hover:bg-[#FFFFFF1A] hover:bg- transition-colors"
                  onClick={() => handleMemberClick(user)}
                >
                  <div className="flex items-center gap-3.75">
                    <Avatar
                      src={user.profileCoverImage ?? undefined}
                      fallback={user.name || user.username || "?"}
                      size="lg"
                      className="!w-[55px] !h-[55px] !text-base"
                    />
                    <div className="flex flex-col gap-[1px] min-w-0">
                      <p className="text-sm font-semibold text-primary truncate">
                        {user.name || user.username}
                      </p>
                      {user.username && user.name && (
                        <p className="text-xs leading-5 text-[#f2f0eb]/80 truncate">
                          @{user.username}
                        </p>
                      )}
                    </div>
                  </div>
                  {user.id !== currentUserId && (
                    <div className="flex items-center gap-[10px] flex-shrink-0">
                      <button
                        type="button"
                        className="p-1 text-[#f2f0eb] hover:text-[#f2f0eb]/70 transition-colors disabled:opacity-50"
                        onClick={e => {
                          e.stopPropagation()
                          createConversation(user.id, {
                            onSuccess: conversation => {
                              navigate(`/chats/dm/${conversation.id}`)
                            },
                          })
                        }}
                        disabled={isCreatingConversation}
                        title="Chat"
                      >
                        <ChatBubbleIcon className="w-[20px] h-[20px]" />
                      </button>
                      <button
                        className={`p-1 transition-colors ${
                          user.isFollowing
                            ? "text-brand-olive hover:text-brand-olive/70"
                            : "text-[#f2f0eb] hover:text-[#f2f0eb]/70"
                        }`}
                        onClick={e => handleFollowToggle(e, user)}
                        disabled={isFollowing || isUnfollowing}
                      >
                        {user.isFollowing ? (
                          <HeartFilledIcon className="w-[20px] h-[20px]" />
                        ) : (
                          <HeartOutlineIcon className="w-[20px] h-[20px]" />
                        )}
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            className="p-1 text-[#f2f0eb] hover:text-[#f2f0eb]/70 transition-colors"
                            onClick={e => e.stopPropagation()}
                            aria-label="More actions"
                          >
                            <MoreVertical className="w-[20px] h-[20px]" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            className="text-brand-terracotta gap-2"
                            onClick={e => {
                              e.stopPropagation()
                              setReportUser(user)
                            }}
                          >
                            <WarningIcon size={16} color="#C86757" />
                            Report
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center text-sm text-white/50">
              {searchQuery
                ? "No users found"
                : activeTab === "joined"
                  ? "No members yet"
                  : "No saved users yet"}
            </div>
          )}
        </div>

        {currentData &&
          ((activeTab === "joined" && data && page < data.totalPages) ||
            (activeTab === "saved" && savedData && savedPage < savedData.totalPages)) &&
          !searchQuery && (
            <div className="px-6 pb-6">
              <button
                className="w-full h-9 text-sm text-white/70 border border-white/15 rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
                onClick={handleLoadMore}
                disabled={isCurrentLoading}
              >
                {isCurrentLoading ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
      </DialogContent>
      {reportUser && (
        <ReportModal
          target="USER"
          open={!!reportUser}
          onOpenChange={open => {
            if (!open) setReportUser(null)
          }}
          targetData={{
            id: reportUser.id,
            name: reportUser.name || reportUser.username || "User",
            handle: reportUser.username || "",
            imageUrl: reportUser.profileCoverImage,
          }}
        />
      )}
    </Dialog>
  )
}
